import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CommentsFormTypes } from "models/formTypes";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import {
  ChatView,
  Icon,
  MenuButton,
  RoundButton,
  StaticInfoMessage,
  StyledButton,
  Tags,
  useCheckRelations,
  useFloatingMessages,
  ViewPanel,
} from "components/common";
import { ViewPanelContentSkeleton } from "components/common/skeletons";
import SupportCaseTopCardParticipants from "components/support/_partials/topCardParticipants";
import { toSupportCommentItems } from "components/support/supportCommentItems";
import { getSupportAttachmentTypes } from "components/support/supportAttachmentTypes";
import { useAnnualAccountService, useFeatureService, useSupportService } from "serviceHooks";
import { Grid } from "@mui/material";
//#endregion
import { FeatureConstants, IconDefinitionConstants } from "utils/constants";
import { CSSHelper, FileHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { AuditorCasePanelState, getAuditorCasePanelState } from "./auditorCasePanelState";
import cssExports from "theme/styles/main.module.scss";

const AuditorCaseWidget: React.FC = () => {
  const annualAccountsFinancialYear = useSelector(
    (store: AppState) => store.auth.selectedCompany.annualAccountsFinancialYear
  );
  const { getIsRevisor } = useCheckRelations();
  const isRevisor = getIsRevisor();
  const { hasCompanyFeature } = useFeatureService();
  const annualAccountService = useAnnualAccountService();
  const supportService = useSupportService();
  const { enqueueErrorMessage } = useFloatingMessages();
  const formRef = React.useRef<FormHandles>(null);

  const [panelState, setPanelState] = React.useState<AuditorCasePanelState>({ kind: "hidden" });
  const [supportIssue, setSupportIssue] = React.useState<WintSuperkollAPIModel.SupportIssue>();
  const [hasUnread, setHasUnread] = React.useState<boolean>(false);
  const [isFormFilled, setIsFormFilled] = React.useState<boolean>(false);
  const [isSending, setIsSending] = React.useState<boolean>(false);

  React.useEffect(() => {
    setPanelState({ kind: "hidden" });
    const financialYearId = annualAccountsFinancialYear?.id;
    if (!financialYearId) return;

    let isCurrent = true;
    hasCompanyFeature(FeatureConstants.AuditorCommunicationPanel)
      .then(async (isEnabled) => {
        // 204 when the financial year has no annual report.
        const annualReport = isEnabled
          ? (await annualAccountService.getFinancialStatement(financialYearId)).data || undefined
          : undefined;
        if (isCurrent) setPanelState(getAuditorCasePanelState(isEnabled, isRevisor, annualReport));
      })
      .catch(() => isCurrent && setPanelState({ kind: "hidden" }));

    return () => {
      isCurrent = false;
    };
  }, [annualAccountsFinancialYear?.id]);

  React.useEffect(() => {
    setSupportIssue(undefined);
    if (panelState.kind !== "case") return;

    let isCurrent = true;
    supportService
      .getById(panelState.caseId)
      .then((issue) => {
        if (!isCurrent) return;
        setSupportIssue(issue);
        setHasUnread(issue.isUnreadByCurrentUser);
        if (issue.isUnreadByCurrentUser) supportService.confirm(issue.id).catch(() => {});
      })
      .catch(() => isCurrent && setPanelState({ kind: "hidden" }));

    return () => {
      isCurrent = false;
    };
  }, [panelState]);

  if (panelState.kind === "hidden" || !annualAccountsFinancialYear) return null;

  const getImageContent = (id: number | string) => supportService.getAttachment(supportIssue!.id, id as number);

  const onFileClick = (id: number | string) => {
    supportService.downloadAttachment(supportIssue!.id, id as number).then((res) => FileHelper.download(res));
  };

  const onExport = () =>
    supportService
      .exportConversation(supportIssue!.id)
      .then((res) => FileHelper.download(res))
      .catch(() =>
        enqueueErrorMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.AuditorCaseDownloadFailed)
        )
      );

  const handleSubmit = (values: CommentsFormTypes.CommentWithFile) =>
    supportService.addCommentAsync(
      supportIssue!.id,
      supportService.convertToSupportCommentPost(
        UtilsHelper.isEmpty(values.explanation)
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.NoMessage)
          : values.explanation,
        values.filesToUpload
      )
    );

  const onFormUpdate = (values: CommentsFormTypes.CommentWithFile) => {
    setIsFormFilled(!UtilsHelper.isEmpty(values.explanation) || !!values.filesToUpload?.length);
  };

  const onSend = async () => {
    setIsSending(true);
    const sentValues = formRef.current?.getValues?.();
    let posted;
    try {
      posted = await formRef.current?.submit();
    } catch {
      enqueueErrorMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.AuditorCaseSendFailed)
      );
    } finally {
      setIsSending(false);
    }
    if (!posted) return;

    // The reply box stays editable while posting; keep anything typed or attached after Skicka.
    if (formRef.current?.getValues?.() === sentValues) formRef.current?.resetForm?.();
    setHasUnread(false);
    supportService
      .getById(supportIssue!.id)
      .then(setSupportIssue)
      .catch(() => {});
  };

  const topRightContent =
    panelState.kind === "case" && supportIssue ? (
      <Grid item display="flex" gap={1} alignItems="center">
        {hasUnread && (
          <Tags
            tags={[LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.AuditorCaseUnread)]}
            className={CSSHelper.getTagClass(StyleEnums.DisplayType.Info)}
          />
        )}
        <MenuButton
          menuPlacement="bottom-end"
          button={
            <RoundButton size="xs" className={cssExports.auditorCaseMoreActions}>
              <Icon icon={IconDefinitionConstants.WMore} />
            </RoundButton>
          }
          menuItems={[
            {
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.AuditorCaseDownload),
              icon: IconDefinitionConstants.WDownload,
              onClickHandler: onExport,
            },
          ]}
        />
      </Grid>
    ) : undefined;

  const renderContent = () => {
    if (panelState.kind === "noCase") {
      return (
        <StaticInfoMessage fullWidth>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.AuditorCaseNoCase)}
        </StaticInfoMessage>
      );
    }

    if (!supportIssue) return <ViewPanelContentSkeleton />;

    return (
      <Grid container direction="column" rowGap={2}>
        <ChatView
          ref={formRef}
          parentId={supportIssue.id}
          comments={toSupportCommentItems(
            supportIssue.comments,
            LocalizationHelperInstance.getMessageForAllLocales(TranslationKeys.SupportIssue.NoMessage)
          )}
          getImageContent={getImageContent}
          onFileClick={onFileClick}
          initialValues={{ postWithoutPicture: true }}
          handleSubmit={handleSubmit}
          commentOrFileRequired
          onFormUpdate={onFormUpdate}
          isReadonly={supportIssue.isReadOnly}
          answerLabel={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.AuditorCaseAnswerLabel)}
          answerMinRows={2}
          hideErrorsUntilSubmit
          keepScrollOnAttachmentsOpen
          attachmentTypes={getSupportAttachmentTypes(isRevisor)}
          hideBottomDivider
          defaultViewCount={3}
          viewCountIncreaseBy={10}
          viewCountIncreaseButtonText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ShowMore)}
        />
        {!supportIssue.isReadOnly && (
          <Grid container justifyContent="flex-end">
            <Grid item>
              <StyledButton
                displayType={StyleEnums.DisplayType.Info}
                size={"sm"}
                onClick={onSend}
                disabled={!isFormFilled}
                isSubmitting={isSending}
                testId="auditorCaseSend"
              >
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Send)}
              </StyledButton>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <ViewPanel
      icon={IconDefinitionConstants.WCommentDots}
      title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.AuditorCaseTitle)}
      description={supportIssue ? <SupportCaseTopCardParticipants supportIssue={supportIssue} /> : undefined}
      topRightContent={topRightContent}
      appearance="lg"
      gridSize="lg"
      className={cssExports.auditorCaseWidget}
    >
      {renderContent()}
    </ViewPanel>
  );
};

export default AuditorCaseWidget;
